import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { AnalyticProfileContext } from '../contexts/AnalyticProfileContext';
import { UserContext } from '../contexts/UserContext';
import {
  ListenTarget,
  ListenTargetKind,
  ListeningConfig,
  ListeningPost,
  ListeningReviewStatus,
  PollJob,
  createListenTarget,
  deleteListenTarget,
  getLatestPollJob,
  getListeningConfig,
  getPollJob,
  getSentimentLexicon,
  listListenTargets,
  listListeningPosts,
  pollListeningNow,
  putSentimentLexicon,
  reanalyzeListeningPosts,
  reviewListeningPost,
  setListeningEnabled,
} from '../api/socialListening';
import { inputDarkSx, neutralChipSx, paperSx } from '../components/socialListening/styles';
import ListeningSchedule, { intervalLabel } from '../components/socialListening/ListeningSchedule';

const ALL_PLATFORMS = [
  { id: 'x', label: 'X / Twitter' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'telegram', label: 'Telegram' },
  { id: 'youtube', label: 'YouTube' },
] as const;

function sentimentChipProps(s: string): {
  color?: 'success' | 'error';
  sx?: typeof neutralChipSx;
} {
  if (s === 'Positive') return { color: 'success' };
  if (s === 'Negative') return { color: 'error' };
  return { sx: neutralChipSx };
}

type ReviewFilter = 'all' | 'unreviewed' | 'confirmed' | 'dismissed';

function reviewButtonSx(active: boolean, tone: 'confirm' | 'dismiss') {
  const activeColor = tone === 'confirm' ? '#22c55e' : '#ef4444';
  const activeText = tone === 'confirm' ? '#052e16' : '#450a0a';
  return {
    color: active ? activeText : '#a1a1aa',
    bgcolor: active ? activeColor : 'transparent',
    border: '1px solid',
    borderColor: active ? activeColor : '#52525b',
    '&:hover': { bgcolor: active ? activeColor : '#27272a' },
    '&.Mui-disabled': { color: '#52525b', borderColor: '#3f3f46' },
  };
}

function linesToList(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

const LayoutSocialListening: React.FC = () => {
  const { selectedProfileId, selectedProfile } = useContext(AnalyticProfileContext);
  const { user } = useContext(UserContext);
  // social_listening.read is required to reach this page; update allows changes and polls (which cost money).
  const canEdit = Boolean(user?.permissions?.social_listening?.update);
  const profileId = selectedProfileId;

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollInfo, setPollInfo] = useState<string | null>(null);
  const [pollJob, setPollJob] = useState<PollJob | null>(null);
  const polling = pollJob?.status === 'running';

  const [enabled, setEnabled] = useState(false);
  const [config, setConfig] = useState<ListeningConfig | null>(null);
  const [targets, setTargets] = useState<ListenTarget[]>([]);
  const [posts, setPosts] = useState<ListeningPost[]>([]);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [positiveText, setPositiveText] = useState('');
  const [negativeText, setNegativeText] = useState('');

  const [kind, setKind] = useState<ListenTargetKind>('topic');
  const [platforms, setPlatforms] = useState<string[]>(['x', 'instagram', 'facebook', 'tiktok']);
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [excludeText, setExcludeText] = useState('');
  const [requireText, setRequireText] = useState('');
  const [queryRewrite, setQueryRewrite] = useState('');
  const [facebookPageId, setFacebookPageId] = useState('');

  const profileName = selectedProfile?.name ?? 'this profile';

  const loadAll = useCallback(async () => {
    if (!profileId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [cfg, t, lex, p, job] = await Promise.all([
        getListeningConfig(profileId),
        listListenTargets(profileId),
        getSentimentLexicon(profileId),
        listListeningPosts(profileId),
        getLatestPollJob(profileId).catch(() => null),
      ]);
      // Resume following a poll that is still running (manual from another tab, or the hourly cron).
      setPollJob(job?.status === 'running' ? job : null);
      setEnabled(Boolean(cfg.enabled));
      setConfig(cfg);
      setTargets(t);
      setPositiveText((lex.positivePhrases ?? []).join('\n'));
      setNegativeText((lex.negativePhrases ?? []).join('\n'));
      setPosts(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load social listening data');
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    setReviewFilter('all');
  }, [profileId]);

  // Follow a running poll job: refresh posts as fetches complete, report when it finishes.
  const pollJobId = polling ? pollJob?.id : undefined;
  useEffect(() => {
    if (!pollJobId || !profileId) return;
    let cancelled = false;
    let lastDone = -1;
    const tick = async () => {
      try {
        const job = await getPollJob(pollJobId);
        if (cancelled) return;
        setPollJob(job);
        if (job.done !== lastDone || job.status !== 'running') {
          lastDone = job.done;
          const p = await listListeningPosts(profileId);
          if (!cancelled) setPosts(p);
        }
        if (job.status === 'running') return;
        const r = job.result;
        if (job.status === 'failed') {
          setError(`Poll failed: ${job.error ?? 'unknown error'}`);
        } else {
          setPollInfo(
            `Fetched ${r.fetched}, inserted ${r.inserted}, skipped ${r.skipped}` +
              (r.errors ? `, errors ${r.errors}` : '')
          );
        }
        const [t, cfg] = await Promise.all([listListenTargets(profileId), getListeningConfig(profileId)]);
        if (cancelled) return;
        setTargets(t);
        setConfig(cfg);
      } catch {
        // Transient network error: keep polling.
      }
    };
    const id = window.setInterval(() => void tick(), 2000);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [pollJobId, profileId]);

  const sentimentCounts = useMemo(() => {
    const counts = { Positive: 0, Neutral: 0, Negative: 0 };
    for (const p of posts) {
      if (p.sentiment === 'Positive') counts.Positive += 1;
      else if (p.sentiment === 'Negative') counts.Negative += 1;
      else counts.Neutral += 1;
    }
    return counts;
  }, [posts]);

  const reviewCounts = useMemo(() => {
    const counts = { confirmed: 0, dismissed: 0, unreviewed: 0 };
    for (const p of posts) {
      if (p.reviewStatus === 'confirmed') counts.confirmed += 1;
      else if (p.reviewStatus === 'dismissed') counts.dismissed += 1;
      else counts.unreviewed += 1;
    }
    return counts;
  }, [posts]);

  const visiblePosts = useMemo(() => {
    if (reviewFilter === 'all') return posts;
    return posts.filter((p) => {
      if (reviewFilter === 'confirmed') return p.reviewStatus === 'confirmed';
      if (reviewFilter === 'dismissed') return p.reviewStatus === 'dismissed';
      return p.reviewStatus !== 'confirmed' && p.reviewStatus !== 'dismissed';
    });
  }, [posts, reviewFilter]);

  const onReview = async (post: ListeningPost, status: ListeningReviewStatus) => {
    setReviewingId(post.id);
    setError(null);
    try {
      const updated = await reviewListeningPost(post.id, status);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? {
                ...p,
                ...updated,
                reviewStatus: updated.reviewStatus ?? '',
                reviewedAt: updated.reviewedAt,
                reviewedBy: updated.reviewedBy,
              }
            : p
        )
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save review');
    } finally {
      setReviewingId(null);
    }
  };

  const togglePlatform = (id: string) => {
    setPlatforms((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const onToggleEnabled = async (next: boolean) => {
    if (!profileId) return;
    setSaving(true);
    setError(null);
    try {
      const cfg = await setListeningEnabled(profileId, next);
      setEnabled(Boolean(cfg.enabled));
      setConfig(cfg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update listening setting');
    } finally {
      setSaving(false);
    }
  };

  const onAddTarget = async () => {
    if (!profileId || !value.trim() || platforms.length === 0) return;
    if (kind === 'handle' && platforms.includes('facebook') && !facebookPageId.trim()) {
      setError('Facebook handle listening needs a Facebook page ID (numeric page_id from Meta).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const t = await createListenTarget({
        profileId,
        kind,
        value: value.trim(),
        label: label.trim() || undefined,
        platforms,
        excludeTerms: linesToList(excludeText),
        requireAny: linesToList(requireText),
        queryRewrite: queryRewrite.trim() || undefined,
        facebookPageId: facebookPageId.trim() || undefined,
      });
      setTargets((prev) => [t, ...prev]);
      setValue('');
      setLabel('');
      setExcludeText('');
      setRequireText('');
      setQueryRewrite('');
      setFacebookPageId('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add target');
    } finally {
      setSaving(false);
    }
  };

  const onDeleteTarget = async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      await deleteListenTarget(id);
      setTargets((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete target');
    } finally {
      setSaving(false);
    }
  };

  const onSaveLexicon = async () => {
    if (!profileId) return;
    setSaving(true);
    setError(null);
    try {
      const lex = await putSentimentLexicon({
        profileId,
        positivePhrases: linesToList(positiveText),
        negativePhrases: linesToList(negativeText),
      });
      setPositiveText((lex.positivePhrases ?? []).join('\n'));
      setNegativeText((lex.negativePhrases ?? []).join('\n'));
      await reanalyzeListeningPosts(profileId);
      setPosts(await listListeningPosts(profileId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save lexicon');
    } finally {
      setSaving(false);
    }
  };

  const onPollNow = async () => {
    if (!profileId) return;
    setError(null);
    setPollInfo(null);
    try {
      setPollJob(await pollListeningNow(profileId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start poll');
    }
  };

  const pollLabel = polling
    ? `Polling${pollJob && pollJob.total > 0 ? ` ${pollJob.done}/${pollJob.total}` : '…'}`
    : 'Poll now';

  if (!profileId) {
    return (
      <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
        <Alert severity="info">Select an analytic profile to configure social media listening.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto', color: '#f4f4f5' }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#fafafa' }}>
          Social Listening
        </Typography>
        <Typography variant="body1" sx={{ color: '#a1a1aa' }}>
          Monitor topics, hashtags, and handles, plus public Telegram channels and YouTube channels, for the selected
          analytic profile. Mentions are collected automatically; sentiment is scored in Flock (optional
          custom lexicon).
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {pollInfo && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPollInfo(null)}>
          {pollInfo}
        </Alert>
      )}
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          View only. Changing targets, the lexicon or the schedule, and running polls, needs the Social Listening
          “update” permission.
        </Alert>
      )}

      <Paper sx={{ ...paperSx, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h6" sx={{ color: '#fafafa' }}>
              Enable listening
            </Typography>
            <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
              When on, Flock polls enabled targets on the schedule (Schedule tab) and you can run Poll now.
            </Typography>
            {enabled && config && (
              <Typography variant="body2" sx={{ color: '#d4d4d8', mt: 0.5 }}>
                Now:{' '}
                {config.currentIntervalMinutes > 0
                  ? intervalLabel(config.currentIntervalMinutes).toLowerCase()
                  : 'paused by schedule'}
                {' · '}
                {config.nextRunAt
                  ? `next poll ${format(new Date(config.nextRunAt), 'EEE d MMM, HH:mm')}`
                  : 'no polls scheduled in the next 31 days'}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={polling ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
              disabled={saving || polling || !enabled || !canEdit}
              onClick={() => void onPollNow()}
              sx={{ borderColor: '#52525b', color: '#e4e4e7' }}
            >
              {pollLabel}
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  disabled={saving || !canEdit}
                  onChange={(_, next) => void onToggleEnabled(next)}
                  color="primary"
                />
              }
              label={enabled ? 'On' : 'Off'}
              sx={{ color: '#e4e4e7', m: 0 }}
            />
          </Stack>
        </Stack>
        {polling && pollJob && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant={pollJob.total > 0 ? 'determinate' : 'indeterminate'}
              value={pollJob.total > 0 ? (pollJob.done / pollJob.total) * 100 : 0}
            />
            <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
              {pollJob.trigger === 'cron' ? 'Scheduled poll' : 'Poll'} running
              {pollJob.total > 0 ? ` — ${pollJob.done} of ${pollJob.total} fetches done` : ''}. New mentions appear
              below as they arrive.
            </Typography>
          </Box>
        )}
        {!enabled && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Listening is off. Configure targets and lexicon anytime; turn on to fetch mentions.
          </Alert>
        )}
      </Paper>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          '& .MuiTab-root': { color: '#a1a1aa', textTransform: 'none' },
          '& .Mui-selected': { color: '#fafafa' },
          '& .MuiTabs-indicator': { backgroundColor: '#818cf8' },
        }}
      >
        <Tab label="Targets" />
        <Tab label={`Feed (${posts.length})`} />
        <Tab label="Sentiment lexicon" />
        <Tab label="Schedule" />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={2}>
          {canEdit && (
            <Paper sx={paperSx}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Add a listen target
              </Typography>
              <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 2 }}>
                Topic and hashtag search X, Instagram, Facebook, and TikTok. Telegram and YouTube follow a public
                channel: use @channel, a t.me link, a YouTube channel URL, or a single video link.
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 140, ...inputDarkSx }}>
                    <InputLabel>Kind</InputLabel>
                    <Select
                      label="Kind"
                      value={kind}
                      onChange={(e) => setKind(e.target.value as ListenTargetKind)}
                    >
                      <MenuItem value="topic">Topic</MenuItem>
                      <MenuItem value="hashtag">Hashtag</MenuItem>
                      <MenuItem value="handle">Handle</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label={kind === 'hashtag' ? 'Hashtag' : kind === 'handle' ? 'Handle' : 'Topic'}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={
                      platforms.includes('telegram')
                        ? '@channel or t.me/channel'
                        : platforms.includes('youtube')
                          ? '@channel or a YouTube URL'
                          : kind === 'hashtag'
                            ? 'campaign2026'
                            : kind === 'handle'
                              ? 'newsorg'
                              : 'climate policy'
                    }
                    sx={{ flex: 1, ...inputDarkSx }}
                  />
                  <TextField
                    size="small"
                    label="Label (optional)"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    sx={{ flex: 1, ...inputDarkSx }}
                  />
                </Stack>

                <FormGroup row>
                  {ALL_PLATFORMS.map((p) => (
                    <FormControlLabel
                      key={p.id}
                      control={
                        <Checkbox
                          checked={platforms.includes(p.id)}
                          onChange={() => togglePlatform(p.id)}
                          sx={{ color: '#a1a1aa', '&.Mui-checked': { color: '#818cf8' } }}
                        />
                      }
                      label={p.label}
                      sx={{ color: '#e4e4e7' }}
                    />
                  ))}
                </FormGroup>

                {kind === 'handle' && platforms.includes('facebook') && (
                  <TextField
                    size="small"
                    label="Facebook page ID"
                    value={facebookPageId}
                    onChange={(e) => setFacebookPageId(e.target.value)}
                    placeholder="Numeric page_id"
                    helperText="Required for Facebook handle listening."
                    sx={inputDarkSx}
                    FormHelperTextProps={{ sx: { color: '#71717a' } }}
                  />
                )}

                <Alert severity="info" sx={{ bgcolor: '#18181b', color: '#d4d4d8', border: '1px solid #3f3f46' }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Optional query hygiene
                  </Typography>
                  <Typography variant="body2" component="div">
                    Homonyms (e.g. a word that also means something unrelated) can pull noisy posts. Use{' '}
                    <strong>exclude</strong> to drop posts containing a word, <strong>require any</strong> so
                    at least one phrase must appear, or <strong>query rewrite</strong> to send a clearer search
                    string while keeping the display value above.
                  </Typography>
                </Alert>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    size="small"
                    label="Exclude terms (one per line)"
                    multiline
                    minRows={2}
                    value={excludeText}
                    onChange={(e) => setExcludeText(e.target.value)}
                    placeholder={'ship\nboat'}
                    sx={{ flex: 1, ...inputDarkSx }}
                  />
                  <TextField
                    size="small"
                    label="Require any (one per line)"
                    multiline
                    minRows={2}
                    value={requireText}
                    onChange={(e) => setRequireText(e.target.value)}
                    placeholder={'competitor\nunrelated'}
                    sx={{ flex: 1, ...inputDarkSx }}
                  />
                  <TextField
                    size="small"
                    label="Query rewrite (optional)"
                    value={queryRewrite}
                    onChange={(e) => setQueryRewrite(e.target.value)}
                    placeholder="Only for topic searches"
                    sx={{ flex: 1, ...inputDarkSx }}
                  />
                </Stack>

                <Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={saving || !value.trim() || platforms.length === 0}
                    onClick={() => void onAddTarget()}
                    sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                  >
                    Add target
                  </Button>
                </Box>
              </Stack>
            </Paper>
          )}

          <Paper sx={paperSx}>
            {targets.length === 0 ? (
              <Typography sx={{ color: '#a1a1aa' }}>No listening targets yet.</Typography>
            ) : (
              <Stack divider={<Divider sx={{ borderColor: '#3f3f46' }} />} spacing={1.5}>
                {targets.map((t) => (
                  <Stack
                    key={t.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={t.kind} sx={{ bgcolor: '#27272a', color: '#e4e4e7' }} />
                        {(t.platforms ?? []).map((p) => (
                          <Chip
                            key={p}
                            size="small"
                            label={p}
                            variant="outlined"
                            sx={{ borderColor: '#52525b', color: '#a1a1aa' }}
                          />
                        ))}
                        <Typography sx={{ fontWeight: 600 }}>
                          {t.kind === 'hashtag' ? `#${t.value}` : t.kind === 'handle' ? `@${t.value}` : t.value}
                        </Typography>
                        {t.label ? (
                          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                            {t.label}
                          </Typography>
                        ) : null}
                      </Stack>
                      {(t.excludeTerms?.length || t.requireAny?.length || t.queryRewrite) && (
                        <Typography variant="caption" sx={{ color: '#71717a' }}>
                          Hygiene
                          {t.excludeTerms?.length ? ` · exclude ${t.excludeTerms.length}` : ''}
                          {t.requireAny?.length ? ` · require ${t.requireAny.length}` : ''}
                          {t.queryRewrite ? ` · rewrite “${t.queryRewrite}”` : ''}
                        </Typography>
                      )}
                      {t.lastError ? (
                        <Typography variant="caption" sx={{ color: '#f87171' }}>
                          Last poll error: {t.lastError}
                        </Typography>
                      ) : t.lastPolledAt ? (
                        <Typography variant="caption" sx={{ color: '#71717a' }}>
                          Last polled {format(new Date(t.lastPolledAt), 'MMM d, yyyy · p')}
                        </Typography>
                      ) : null}
                    </Stack>
                    {canEdit && (
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<DeleteIcon />}
                        onClick={() => void onDeleteTarget(t.id)}
                        sx={{ color: '#f87171' }}
                      >
                        Remove
                      </Button>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Chip label={`Positive ${sentimentCounts.Positive}`} color="success" size="small" />
            <Chip label={`Neutral ${sentimentCounts.Neutral}`} size="small" sx={neutralChipSx} />
            <Chip label={`Negative ${sentimentCounts.Negative}`} color="error" size="small" />
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => void loadAll()}
              sx={{ color: '#a1a1aa' }}
            >
              Refresh
            </Button>
            <Button
              size="small"
              startIcon={polling ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
              disabled={saving || polling || !enabled || !canEdit}
              onClick={() => void onPollNow()}
              sx={{ color: '#a1a1aa' }}
            >
              {pollLabel}
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            {(
              [
                ['all', `All ${posts.length}`],
                ['unreviewed', `Unreviewed ${reviewCounts.unreviewed}`],
                ['confirmed', `Confirmed ${reviewCounts.confirmed}`],
                ['dismissed', `Dismissed ${reviewCounts.dismissed}`],
              ] as const
            ).map(([id, label]) => (
              <Chip
                key={id}
                size="small"
                label={label}
                onClick={() => setReviewFilter(id)}
                variant={reviewFilter === id ? 'filled' : 'outlined'}
                sx={
                  reviewFilter === id
                    ? { bgcolor: '#6366f1', color: '#eef2ff' }
                    : { borderColor: '#52525b', color: '#a1a1aa' }
                }
              />
            ))}
          </Stack>

          <Paper sx={paperSx}>
            {posts.length === 0 ? (
              <Typography sx={{ color: '#a1a1aa' }}>
                No mentions yet. Enable listening, add targets, then Poll now (or wait for the hourly cron).
              </Typography>
            ) : visiblePosts.length === 0 ? (
              <Typography sx={{ color: '#a1a1aa' }}>No mentions match this review filter.</Typography>
            ) : (
              <Stack divider={<Divider sx={{ borderColor: '#3f3f46' }} />} spacing={2}>
                {visiblePosts.map((p) => (
                  <Box
                    key={p.id}
                    sx={{
                      borderLeft: '3px solid',
                      borderColor:
                        p.reviewStatus === 'confirmed'
                          ? '#22c55e'
                          : p.reviewStatus === 'dismissed'
                            ? '#ef4444'
                            : 'transparent',
                      pl: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={p.sentiment} {...sentimentChipProps(p.sentiment)} />
                      <Chip
                        size="small"
                        label={p.platform}
                        variant="outlined"
                        sx={{ borderColor: '#52525b', color: '#a1a1aa' }}
                      />
                      {p.author ? (
                        <Typography variant="body2" sx={{ color: '#d4d4d8' }}>
                          @{p.author}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" sx={{ color: '#71717a' }}>
                        {p.postedAt ? format(new Date(p.postedAt), 'MMM d, yyyy · p') : ''}
                      </Typography>
                      {p.url ? (
                        <Link href={p.url} target="_blank" rel="noopener noreferrer" variant="caption">
                          Open
                        </Link>
                      ) : null}
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <Tooltip
                          title={
                            !canEdit
                              ? 'Needs Social Listening update permission'
                              : p.reviewStatus === 'confirmed'
                                ? 'Confirmed. Click to undo.'
                                : 'Confirm as a good mention'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Confirm mention"
                              aria-pressed={p.reviewStatus === 'confirmed'}
                              disabled={!canEdit || reviewingId === p.id}
                              onClick={() =>
                                void onReview(p, p.reviewStatus === 'confirmed' ? '' : 'confirmed')
                              }
                              sx={reviewButtonSx(p.reviewStatus === 'confirmed', 'confirm')}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            !canEdit
                              ? 'Needs Social Listening update permission'
                              : p.reviewStatus === 'dismissed'
                                ? 'Dismissed. Click to undo.'
                                : 'Dismiss as not appropriate'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Dismiss mention"
                              aria-pressed={p.reviewStatus === 'dismissed'}
                              disabled={!canEdit || reviewingId === p.id}
                              onClick={() =>
                                void onReview(p, p.reviewStatus === 'dismissed' ? '' : 'dismissed')
                              }
                              sx={reviewButtonSx(p.reviewStatus === 'dismissed', 'dismiss')}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Stack>
                    <Typography
                      sx={{ whiteSpace: 'pre-wrap', opacity: p.reviewStatus === 'dismissed' ? 0.55 : 1 }}
                    >
                      {p.text}
                    </Typography>
                    {p.matchedPhrases?.length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        {p.matchedPhrases.map((phrase) => (
                          <Chip
                            key={phrase}
                            size="small"
                            label={phrase}
                            sx={{ bgcolor: '#312e81', color: '#e0e7ff' }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      )}

      {tab === 2 && (
        <Paper sx={paperSx}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Profile-specific sentiment phrases (optional)
          </Typography>
          <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 2 }}>
            Leave empty to use Flock&apos;s base scorer only. One phrase per line — e.g. a competitor as
            negative, or a campaign slogan as positive. Saving re-scores existing posts for{' '}
            <strong style={{ color: '#e4e4e7' }}>{profileName}</strong>.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Positive phrases"
              multiline
              minRows={8}
              value={positiveText}
              onChange={(e) => setPositiveText(e.target.value)}
              placeholder={'product launch\nrecord audience'}
              fullWidth
              sx={inputDarkSx}
            />
            <TextField
              label="Negative phrases"
              multiline
              minRows={8}
              value={negativeText}
              onChange={(e) => setNegativeText(e.target.value)}
              placeholder={'budget cut\nlayoffs'}
              fullWidth
              sx={inputDarkSx}
            />
          </Stack>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={saving || !canEdit}
              onClick={() => void onSaveLexicon()}
              sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
            >
              Save lexicon &amp; reanalyze
            </Button>
          </Box>
        </Paper>
      )}

      {tab === 3 && (
        <ListeningSchedule
          profileId={profileId}
          config={config}
          targets={targets}
          readOnly={!canEdit}
          onSaved={(cfg) => {
            setConfig(cfg);
            setPollInfo('Schedule saved');
          }}
        />
      )}
    </Box>
  );
};

export default LayoutSocialListening;
